import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import {
  Upload, Paperclip, Trash2, Download, FileText, FileImage, FileSpreadsheet,
  AlertCircle, X, Loader2, FileType as FileIcon,
} from "lucide-react";
import { API } from "@/contexts/AuthContext";

const MAX_MB = 25;
const MAX_BYTES = MAX_MB * 1024 * 1024;
const ACCEPTED = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.odt,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif";

function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

function iconForType(ct) {
  if (!ct) return FileIcon;
  if (ct.startsWith("image/")) return FileImage;
  if (ct.includes("pdf") || ct.includes("word") || ct.includes("text/")) return FileText;
  if (ct.includes("sheet") || ct.includes("excel") || ct.includes("csv")) return FileSpreadsheet;
  return FileIcon;
}

/**
 * AttachmentManager
 *
 * Props:
 *  - scope: "client" | "task"
 *  - parentId: clientId (when scope==="client") or taskId (when scope==="task")
 *  - title: heading text
 *  - subtitle: optional small text under heading
 *  - compact: smaller layout for inline use inside a task row
 */
export default function AttachmentManager({ scope, parentId, title, subtitle, compact = false }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const baseUrl = scope === "client"
    ? `${API}/clients/${parentId}/attachments`
    : `${API}/tasks/${parentId}/attachments`;

  const load = useCallback(async () => {
    if (!parentId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(baseUrl);
      setFiles(data || []);
    } catch (e) {
      console.error("Failed to load attachments:", e);
    } finally {
      setLoading(false);
    }
  }, [baseUrl, parentId]);

  useEffect(() => { load(); }, [load]);

  const upload = async (file, description = "") => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setError(`"${file.name}" is too large (${formatBytes(file.size)}). Max ${MAX_MB} MB per file.`);
      return;
    }
    setError("");
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (description) fd.append("description", description);
      await axios.post(baseUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Upload failed. Please try again.");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const onSelect = (e) => {
    const list = Array.from(e.target.files || []);
    list.forEach((f) => upload(f));
    e.target.value = ""; // reset so same file can be re-selected later
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const list = Array.from(e.dataTransfer?.files || []);
    list.forEach((f) => upload(f));
  };

  const download = async (att) => {
    try {
      const res = await axios.get(`${API}/attachments/${att.id}/download`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: att.content_type || "application/octet-stream" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.original_filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Download failed:", e);
      alert("Couldn't download file.");
    }
  };

  const remove = async (att) => {
    if (!window.confirm(`Delete "${att.original_filename}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/attachments/${att.id}`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't delete file.");
    }
  };

  // ── Compact layout (for inline use in task rows) ───────────────────────────
  if (compact) {
    return (
      <div className="space-y-2" data-testid={`attachments-compact-${parentId}`}>
        {files.map((f) => {
          const Icon = iconForType(f.content_type);
          return (
            <div key={f.id} className="flex items-center gap-2 text-xs bg-[#F1E4D5] rounded-lg px-3 py-1.5 group" data-testid={`attachment-row-${f.id}`}>
              <Icon className="w-3.5 h-3.5 text-[#EA823A] shrink-0" />
              <button onClick={() => download(f)} className="font-medium text-[#284952] hover:text-[#E2718A] truncate flex-1 text-left" data-testid={`download-${f.id}`}>
                {f.original_filename}
              </button>
              <span className="text-[#6B7A80] text-[10px] tabular-nums">{formatBytes(f.size)}</span>
              <button onClick={() => remove(f)} className="text-[#6B7A80] hover:text-red-500 opacity-0 group-hover:opacity-100 transition" data-testid={`delete-${f.id}`} aria-label="Delete">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          );
        })}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="w-full text-xs px-3 py-2 rounded-lg border border-dashed border-[#F0E5DE] hover:border-[#E2718A] hover:bg-[#F1E4D5] text-[#6B7A80] hover:text-[#E2718A] transition flex items-center justify-center gap-2"
          data-testid={`upload-trigger-${parentId}`}
        >
          {uploading ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading {progress}%</>
          ) : (
            <><Paperclip className="w-3.5 h-3.5" /> Attach file</>
          )}
        </button>
        <input ref={inputRef} type="file" accept={ACCEPTED} onChange={onSelect} className="hidden" />
        {error && (
          <div className="flex items-center gap-2 text-xs text-red-600">
            <AlertCircle className="w-3.5 h-3.5" /> {error}
            <button onClick={() => setError("")} className="ml-auto"><X className="w-3 h-3" /></button>
          </div>
        )}
      </div>
    );
  }

  // ── Full layout (Documents tab) ────────────────────────────────────────────
  return (
    <div className="bg-white rounded-3xl border border-[#F0E5DE] p-7" data-testid={`attachments-${parentId}`}>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h2 className="font-display text-2xl text-[#1F2A2E] flex items-center gap-3">
            <Paperclip className="w-5 h-5 text-[#E2718A]" /> {title}
          </h2>
          {subtitle && <p className="text-sm text-[#6B7A80] mt-1">{subtitle}</p>}
        </div>
        <div className="text-xs text-[#6B7A80]">Max {MAX_MB} MB per file. PDF / Word / Excel / images.</div>
      </div>

      {/* Drop zone */}
      <div
        onDrop={onDrop}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-2xl border-2 border-dashed p-8 mb-5 text-center transition ${
          dragging ? "border-[#E2718A] bg-[#FCE7EB]" : "border-[#F0E5DE] hover:border-[#E2718A] hover:bg-[#F1E4D5]"
        }`}
        data-testid="dropzone"
      >
        <input ref={inputRef} type="file" accept={ACCEPTED} multiple onChange={onSelect} className="hidden" data-testid="file-input" />
        {uploading ? (
          <>
            <Loader2 className="w-8 h-8 mx-auto mb-3 text-[#E2718A] animate-spin" />
            <div className="font-display text-lg text-[#1F2A2E]">Uploading… {progress}%</div>
            <div className="w-full bg-[#F0E5DE] rounded-full h-1.5 mt-3 max-w-xs mx-auto overflow-hidden">
              <div className="h-full bg-[#E2718A] transition-all" style={{ width: `${progress}%` }} />
            </div>
          </>
        ) : (
          <>
            <Upload className="w-8 h-8 mx-auto mb-3 text-[#EA823A]" />
            <div className="font-display text-lg text-[#1F2A2E] mb-1">
              {dragging ? "Drop to upload" : "Click or drag to upload"}
            </div>
            <div className="text-xs text-[#6B7A80]">
              Accepts PDF, Word, Excel, images. Files are private, only your team can see them.
            </div>
          </>
        )}
      </div>

      {error && (
        <div className="mb-5 p-3 rounded-xl bg-red-50 border border-red-200 flex gap-2 items-start text-sm text-red-700" data-testid="upload-error">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError("")}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* File list */}
      {loading ? (
        <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#E2718A] mx-auto" /></div>
      ) : files.length === 0 ? (
        <div className="text-center py-10 text-sm text-[#6B7A80]" data-testid="empty-documents">
          No documents yet. Upload the client's NDIS plan, signed service agreement, intake forms or reports above.
        </div>
      ) : (
        <ul className="divide-y divide-[#F0E5DE]" data-testid="documents-list">
          {files.map((f) => {
            const Icon = iconForType(f.content_type);
            return (
              <li key={f.id} className="flex items-center gap-4 py-4 group" data-testid={`document-${f.id}`}>
                <div className="w-11 h-11 rounded-xl bg-[#F1E4D5] flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-[#EA823A]" />
                </div>
                <div className="flex-1 min-w-0">
                  <button onClick={() => download(f)} className="font-medium text-[#1F2A2E] hover:text-[#E2718A] transition text-left block truncate w-full" data-testid={`document-download-${f.id}`}>
                    {f.original_filename}
                  </button>
                  <div className="text-xs text-[#6B7A80] mt-0.5">
                    {formatBytes(f.size)} · uploaded {new Date(f.created_at).toLocaleDateString("en-AU")} by {f.uploaded_by_name || "team member"}
                    {f.description && <span> · {f.description}</span>}
                  </div>
                </div>
                <button onClick={() => download(f)} className="w-9 h-9 rounded-full hover:bg-[#F1E4D5] flex items-center justify-center text-[#284952] hover:text-[#E2718A] transition" aria-label="Download" data-testid={`btn-download-${f.id}`}>
                  <Download className="w-4 h-4" />
                </button>
                <button onClick={() => remove(f)} className="w-9 h-9 rounded-full hover:bg-red-50 flex items-center justify-center text-[#6B7A80] hover:text-red-600 transition opacity-0 group-hover:opacity-100" aria-label="Delete" data-testid={`btn-delete-${f.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
